
import { supabase } from '@/integrations/supabase/client';
import { clearSessionData } from './sessionService';
import { createUserProfile, saveUserToStorage } from './userProfileService';
import { UserProfile } from '@/types/auth';

// Debug helper - only logs in development
const logDebug = (message: string, data?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[AuthService] ${message}`, data || '');
  }
};

// Login with email and password
export const loginWithEmailPassword = async (email: string, password: string) => {
  logDebug('Login attempt', { email });
  
  // Form validation
  if (!email.trim()) throw new Error('Email is required');
  if (!password.trim()) throw new Error('Password is required');
  
  try {
    // Clear any existing sessions before login
    await supabase.auth.signOut();
    clearSessionData();
    
    // Sign in with Supabase
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    
    if (error) throw error;
    if (!data.user) throw new Error('No user data received');
    
    logDebug('Login successful', { userId: data.user.id });
    
    // Create user profile from session
    const userProfile = data.session ? await createUserProfile(data.session) : null;
    
    // Ensure auto refresh is started
    try {
      supabase.auth.startAutoRefresh();
      logDebug('Auto refresh started successfully');
    } catch (refreshError) {
      logDebug('Warning: Failed to start auto refresh', refreshError);
      // Continue even if this fails - don't block login
    }
    
    return { data, userProfile };
  } catch (error) {
    logDebug('Login failed', error);
    throw error;
  }
};

// Sign up with email and password
export const signupWithEmailPassword = async (
  name: string, 
  email: string, 
  password: string, 
  phone: string,
  age?: number,
  educationStatus: string = 'completed12th'
) => {
  logDebug('Signup attempt', { email, name });
  
  // Form validation
  if (!name.trim()) throw new Error('Name is required');
  if (!email.trim()) throw new Error('Email is required');
  if (!password.trim()) throw new Error('Password is required');
  if (!phone.trim()) throw new Error('Phone number is required');
  if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
    throw new Error('Phone number must be 10 digits');
  }
  
  try {
    // Clear any existing sessions before signup
    await supabase.auth.signOut();
    clearSessionData();
    
    // Register with Supabase Auth
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          phone,
          age,
          education_status: educationStatus,
        },
        emailRedirectTo: `${window.location.origin}/login`
      }
    });
    
    if (error) throw error;
    if (!data.user) throw new Error('No user data received');
    
    logDebug('Signup successful', { 
      userId: data.user.id,
      hasSession: !!data.session 
    });
    
    // Create a user profile object
    const userProfile: UserProfile = {
      id: data.user.id,
      name,
      email,
      phone: phone || '',
      age: age || 21,
      educationStatus: educationStatus as 'completed10th' | 'completed12th' | 'others',
    };
    
    // If we have a session, start auto refresh
    if (data.session) {
      try {
        supabase.auth.startAutoRefresh();
        saveUserToStorage(userProfile);
        logDebug('Auto refresh started successfully');
      } catch (refreshError) {
        logDebug('Warning: Failed to start auto refresh', refreshError);
        // Continue even if this fails - don't block signup
      }
    }
    
    return { data, userProfile };
  } catch (error) {
    logDebug('Signup failed', error);
    throw error;
  }
};

// Log out user
export const logoutUser = async () => {
  logDebug('Logout attempt');
  
  try {
    await supabase.auth.signOut();
    clearSessionData();
    logDebug('Logout successful');
  } catch (error) {
    logDebug('Logout failed', error);
    throw error;
  }
};

// Get current session
export const getCurrentSession = async () => {
  try {
    return await supabase.auth.getSession();
  } catch (error) {
    logDebug('Error getting current session', error);
    throw error;
  }
};

// Get current user
export const getCurrentUser = async () => {
  try {
    return await supabase.auth.getUser();
  } catch (error) {
    logDebug('Error getting current user', error);
    throw error;
  }
};
